import type { AppSettings } from "@nmt/shared";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, post } from "../../api";
import { Alert, Button, Field, Loading, PageHeader, Panel, PanelHeader, SelectField } from "../../components/ui";
import { ResetCard } from "../../components/reset-card";
import { useSession } from "../../session";
import { applyTheme } from "../../theme";
import { useSettings } from "./useSettings";
import { useUsers } from "../../lookups";

const TIMEZONES = ["America/Chicago", "America/New_York", "America/Denver", "America/Phoenix", "America/Los_Angeles"];
const TIMEOUTS = [15, 30, 60, 120, 240, 480, 720, 1440, 4320, 10080, 20160, 43200];

type Form = Pick<
  AppSettings,
  | "company.name"
  | "company.timezone"
  | "company.phone"
  | "company.email"
  | "company.address"
  | "company.license"
  | "company.signerUserId"
  | "rivian.userId"
  | "branding.primaryColor"
  | "branding.sidebarColor"
  | "security.inactivityTimeoutMinutes"
>;

export function CompanySettings() {
  const { me, can } = useSession();
  const { query, save } = useSettings();
  const [form, setForm] = useState<Form | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const users = useUsers();
  useEffect(() => {
    if (query.data && !form) {
      const s = query.data.settings;
      setForm({
        "company.name": s["company.name"],
        "company.timezone": s["company.timezone"],
        "company.phone": s["company.phone"],
        "company.email": s["company.email"],
        "company.address": s["company.address"],
        "company.license": s["company.license"],
        "company.signerUserId": s["company.signerUserId"],
        "rivian.userId": s["rivian.userId"],
        "branding.primaryColor": s["branding.primaryColor"],
        "branding.sidebarColor": s["branding.sidebarColor"],
        "security.inactivityTimeoutMinutes": s["security.inactivityTimeoutMinutes"],
      });
    }
  }, [query.data, form]);

  // Preview colors as they're picked.
  useEffect(() => {
    if (form) applyTheme({ primaryColor: form["branding.primaryColor"], sidebarColor: form["branding.sidebarColor"] });
  }, [form]);
  useEffect(
    () => () => {
      const s = query.data?.settings;
      if (s) applyTheme({ primaryColor: s["branding.primaryColor"], sidebarColor: s["branding.sidebarColor"] });
    },
    [query.data],
  );

  const logo = useMutation({
    mutationFn: async (file: File) => {
      const up = await post<{ key: string; url: string; headers: Record<string, string> }>("/api/settings/logo-upload", { contentType: file.type });
      const res = await fetch(up.url, { method: "PUT", headers: up.headers, body: file });
      if (!res.ok) throw new Error("Upload failed. Try again.");
      await save.mutateAsync({ "branding.logoKey": up.key });
    },
    onSuccess: () => setNotice("Logo updated."),
  });

  if (query.isLoading || !form) return <Loading />;
  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => f && { ...f, [k]: k === "security.inactivityTimeoutMinutes" ? Number(e.target.value) : e.target.value });
  const error = save.error ?? logo.error;

  return (
    <>
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        setNotice(null);
        save.mutate(form, { onSuccess: () => setNotice("Saved.") });
      }}
    >
      <PageHeader title="Company and theme" />
      {notice && <Alert tone="ok">{notice}</Alert>}
      {error && <Alert>{errorMessage(error)}</Alert>}
      <Panel>
        <PanelHeader title="Company" description="Shown on documents like proposals and work orders as they're built." />
        <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
          <Field label="Company name" value={form["company.name"]} onChange={set("company.name")} required />
          <Field label="License number" value={form["company.license"]} onChange={set("company.license")} />
          <Field label="Phone" value={form["company.phone"]} onChange={set("company.phone")} />
          <Field label="Email" type="email" value={form["company.email"]} onChange={set("company.email")} />
          <Field className="sm:col-span-2" label="Address" value={form["company.address"]} onChange={set("company.address")} />
          <SelectField
            label="Signs proposals for NMT"
            value={form["company.signerUserId"] ?? ""}
            onChange={(e) => setForm({ ...form, "company.signerUserId": e.target.value || null })}
          >
            <option value="">Ryan Etsell (signature on file)</option>
            {(users.data ?? [])
              .filter((u) => u.active)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
          </SelectField>
          <SelectField
            label="Sends addresses to the Rivian"
            hint="The one person who sees Send to Rivian on jobs and contacts (and whose Fred can do it). It's their truck."
            value={form["rivian.userId"] ?? ""}
            onChange={(e) => setForm({ ...form, "rivian.userId": e.target.value || null })}
          >
            <option value="">Nobody</option>
            {(users.data ?? [])
              .filter((u) => u.active)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
          </SelectField>
          <SelectField label="Time zone" value={form["company.timezone"]} onChange={set("company.timezone")}>
            {[...new Set([form["company.timezone"], ...TIMEZONES])].map((tz) => (
              <option key={tz}>{tz}</option>
            ))}
          </SelectField>
        </div>
      </Panel>

      <Panel>
        <PanelHeader title="Theme" description="Colors preview as you pick them; save to keep them for everyone." />
        <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
          <ColorField label="Accent (buttons, highlights)" value={form["branding.primaryColor"]} onChange={(v) => setForm({ ...form, "branding.primaryColor": v })} />
          <ColorField label="Sidebar" value={form["branding.sidebarColor"]} onChange={(v) => setForm({ ...form, "branding.sidebarColor": v })} />
          <div className="sm:col-span-2">
            <div className="mb-1 text-sm font-semibold text-ink-soft">Logo</div>
            <div className="flex items-center gap-4">
              {query.data?.logoUrl ? <img src={query.data.logoUrl} alt="Current logo" className="h-12 rounded bg-mist object-contain p-1" /> : <span className="text-sm text-muted">No logo yet.</span>}
              <label className="cursor-pointer rounded-[var(--radius-control)] border border-line bg-white px-3 py-1.5 text-sm font-semibold hover:border-ink-soft">
                {logo.isPending ? "Uploading…" : "Upload logo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="sr-only"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) logo.mutate(f);
                  }}
                />
              </label>
            </div>
          </div>
          <button
            type="button"
            className="w-fit text-sm text-muted hover:text-ink"
            onClick={() => setForm({ ...form, "branding.primaryColor": "#D71920", "branding.sidebarColor": "#111111" })}
          >
            Reset to NMT red and black
          </button>
        </div>
      </Panel>

      <Panel>
        <PanelHeader title="Security" />
        <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
          <SelectField
            label="Sign out after inactivity"
            value={form["security.inactivityTimeoutMinutes"]}
            onChange={set("security.inactivityTimeoutMinutes")}
            hint="Applies to everyone, including sessions open now."
          >
            {[...new Set([form["security.inactivityTimeoutMinutes"], ...TIMEOUTS])]
              .sort((a, b) => a - b)
              .map((m) => (
                <option key={m} value={m}>
                  {m < 60 ? `${m} minutes` : m < 1440 ? `${m / 60} hour${m > 60 ? "s" : ""}` : `${m / 1440} day${m > 1440 ? "s" : ""}`}
                </option>
              ))}
          </SelectField>
          <p className="text-sm text-muted sm:pt-7">Sign-in and two-step verification are handled by Google Workspace. Make sure 2-step verification is required for everyone in the Google Admin console.</p>
        </div>
      </Panel>

      <div className="flex justify-end">
        <Button variant="primary" type="submit" busy={save.isPending}>
          Save
        </Button>
      </div>
    </form>
    {/* Testing only: the server doesn't have this on production either. */}
    {me?.stage !== "production" && can("roles.manage") && can("settings.manage") && (
      <div className="mt-5">
        <ResetCard
          title="Testing: reset everything"
          description="Empties all the business data (contacts, jobs and everything on them, files, tasks, appointments, texts, receipts, invoices, imports) so testing can start clean. Users, roles, settings, workflows, lists, and connections stay, and you stay signed in. This isn't available on the live site."
          previewUrl="/api/testing/reset/preview"
          resetUrl="/api/testing/reset"
          phrase="RESET EVERYTHING"
          button="Reset everything"
          done="Done. All the business data is gone; setup is as it was."
        />
        <div className="mt-5">
          <ResetCard
            title="Testing: fresh start"
            description="As if the CRM were just installed: removes everything above plus all users, roles, settings, workflows, lists, and connections (QuickBooks, notifications), and signs everyone out. The starting setup is made again, with you as the only user (the admin), so you can sign back in with Google. The activity log stays. This isn't available on the live site."
            previewUrl="/api/testing/fresh-start/preview"
            resetUrl="/api/testing/fresh-start"
            phrase="FRESH START"
            button="Fresh start"
            done=""
            onDone={() => window.location.assign("/")}
          />
        </div>
      </div>
    )}
    </>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-semibold text-ink-soft">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-10 w-14 cursor-pointer rounded border border-line" aria-label={label} />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-28 rounded-[var(--radius-control)] border border-line px-3 py-2 font-mono text-sm uppercase"
          aria-label={`${label} hex`}
        />
      </div>
    </div>
  );
}
